import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/dateUtils.ts';
import { getDarfControl, markDarfPaid, markDarfUnpaid, DarfControlResponse, DarfMonthEntry, DarfYearSummary } from '../services/fiiApi.ts';
import './FIIDarf.css';

const SICALC_URL = 'https://sicalc.receita.fazenda.gov.br/sicalc/rapido/contribuinte';

const formatMonth = (m: string) => {
  const [y, mo] = m.split('-');
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${months[parseInt(mo, 10) - 1]}/${y}`;
};

const formatDate = (d: string) => {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const FIIDarf: React.FC = () => {
  const [data, setData] = useState<DarfControlResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [togglingMonth, setTogglingMonth] = useState<string | null>(null);
  const [showYearSummary, setShowYearSummary] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setError('');
      const result = await getDarfControl();
      setData(result);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao carregar controle de DARF');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleExpand = (month: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(month)) {
        next.delete(month);
      } else {
        next.add(month);
      }
      return next;
    });
  };

  const handleTogglePaid = async (entry: DarfMonthEntry) => {
    setTogglingMonth(entry.reference_month);
    try {
      if (entry.is_paid) {
        await markDarfUnpaid(entry.reference_month);
      } else {
        await markDarfPaid(entry.reference_month);
      }
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao atualizar pagamento');
    } finally {
      setTogglingMonth(null);
    }
  };

  const getStatusClass = (entry: DarfMonthEntry) => {
    if (entry.tax_amount === 0) return 'status-neutral';
    if (entry.should_accumulate) return 'status-accumulate';
    if (entry.is_paid) return 'status-paid';
    if (entry.is_overdue) return 'status-overdue';
    return 'status-pending';
  };

  const getStatusLabel = (entry: DarfMonthEntry) => {
    if (entry.tax_amount === 0) return 'Sem imposto';
    if (entry.should_accumulate) return 'Acumular';
    if (entry.is_paid) return 'Pago';
    if (entry.is_overdue) return 'Vencido';
    return 'Pendente';
  };

  const showDarfActions = (entry: DarfMonthEntry) =>
    entry.darf_amount > 0 && !entry.should_accumulate;

  if (loading) {
    return (
      <div className="fii-darf">
        <div className="page-header">
          <div>
            <h1>Controle DARF</h1>
            <p>Controle de imposto sobre ganho de capital em FIIs</p>
          </div>
        </div>
        <div className="loading-state">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="fii-darf">
      <div className="page-header">
        <div>
          <h1>Controle DARF</h1>
          <p>Controle de imposto sobre ganho de capital em FIIs</p>
        </div>
      </div>

      <div className="info-banner">
        FIIs são tributados a <strong>20% sobre o lucro</strong> na venda de cotas.
        Não há isenção de R$20.000/mês para FIIs. Prejuízos acumulados são compensados automaticamente nos meses seguintes.
        A data limite para pagamento é o <strong>último dia útil do mês subsequente</strong> à venda.
        Quando o valor da DARF for inferior a <strong>R$10,00</strong>, o imposto deve ser acumulado para o período seguinte.
        A corretora retém <strong>0,005% do valor da venda</strong> ("dedo-duro" / IRRF na fonte), valor que é deduzido da DARF apurada — o saldo não compensado pode ser usado em meses seguintes ou restituído na declaração anual.
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {data && (
        <>
          <div className="summary-cards">
            <div className="summary-card">
              <span className="summary-label">Total DARF Devido</span>
              <span className="summary-value">{formatCurrency(data.total_tax_due)}</span>
            </div>
            <div className="summary-card paid">
              <span className="summary-label">Total Pago</span>
              <span className="summary-value">{formatCurrency(data.total_tax_paid)}</span>
            </div>
            <div className={`summary-card ${data.total_tax_pending > 0 ? 'pending' : ''}`}>
              <span className="summary-label">Total Pendente</span>
              <span className="summary-value">{formatCurrency(data.total_tax_pending)}</span>
            </div>
            <div className={`summary-card ${data.total_accumulated_loss > 0 ? 'loss' : ''}`}>
              <span className="summary-label">Prejuízo Acumulado</span>
              <span className="summary-value">{formatCurrency(data.total_accumulated_loss)}</span>
            </div>
            <div className="summary-card">
              <span className="summary-label" title="IRRF retido pela corretora em todas as vendas (dedo-duro)">IRRF Retido (Total)</span>
              <span className="summary-value">{formatCurrency(data.total_irrf_retido)}</span>
            </div>
            <div className={`summary-card ${data.irrf_acumulado_atual > 0 ? 'paid' : ''}`}>
              <span className="summary-label" title="Saldo de IRRF que ainda pode abater DARFs futuras">IRRF a Compensar</span>
              <span className="summary-value">{formatCurrency(data.irrf_acumulado_atual)}</span>
            </div>
          </div>

          {(!data.months || data.months.length === 0) ? (
            <div className="empty-state">
              <p>Nenhuma operação de venda registrada. Registre vendas em Operações para ver o controle de DARF.</p>
            </div>
          ) : (
            <div className="darf-table-container">
              <table className="darf-table">
                <thead>
                  <tr>
                    <th className="col-expand"></th>
                    <th>Mês Ref.</th>
                    <th className="text-right">Vendas</th>
                    <th className="text-right">Resultado</th>
                    <th className="text-right">Prej. Acum.</th>
                    <th className="text-right">Base Tributável</th>
                    <th className="text-right" title="Imposto bruto antes da compensação de IRRF">Imp. Bruto</th>
                    <th className="text-right" title="IRRF retido nas vendas do mês (dedo-duro)">IRRF Mês</th>
                    <th className="text-right" title="IRRF deduzido do imposto deste mês">IRRF Comp.</th>
                    <th className="text-right">Valor da DARF</th>
                    <th>Vencimento</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((entry) => {
                    const isExpanded = expandedMonths.has(entry.reference_month);
                    const statusClass = getStatusClass(entry);
                    const canAct = showDarfActions(entry);

                    return (
                      <React.Fragment key={entry.reference_month}>
                        <tr className={`month-row ${statusClass}`} onClick={() => toggleExpand(entry.reference_month)}>
                          <td className="col-expand">
                            <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                          </td>
                          <td className="month-cell">{formatMonth(entry.reference_month)}</td>
                          <td className="text-right">{formatCurrency(entry.total_sales)}</td>
                          <td className={`text-right ${entry.gross_result >= 0 ? 'positive' : 'negative'}`}>
                            {formatCurrency(entry.gross_result)}
                          </td>
                          <td className="text-right">
                            {entry.accumulated_loss_before > 0 ? (
                              <span className="loss-badge">{formatCurrency(entry.accumulated_loss_before)}</span>
                            ) : '—'}
                          </td>
                          <td className="text-right">
                            {entry.taxable_profit > 0 ? formatCurrency(entry.taxable_profit) : '—'}
                          </td>
                          <td className={`text-right ${entry.tax_amount_before_irrf > 0 ? 'tax-has-value' : ''}`}>
                            {entry.tax_amount_before_irrf > 0 ? formatCurrency(entry.tax_amount_before_irrf) : '—'}
                          </td>
                          <td className="text-right">
                            {entry.irrf_retido_mes > 0 ? formatCurrency(entry.irrf_retido_mes) : '—'}
                          </td>
                          <td className="text-right">
                            {entry.irrf_compensado > 0 ? (
                              <span className="irrf-compensated">−{formatCurrency(entry.irrf_compensado)}</span>
                            ) : '—'}
                          </td>
                          <td className={`text-right tax-cell ${canAct ? 'has-tax' : ''}`}>
                            {entry.darf_amount > 0 ? formatCurrency(entry.darf_amount) : '—'}
                          </td>
                          <td>
                            {entry.should_accumulate || entry.tax_amount === 0 ? '—' : formatDate(entry.due_date)}
                          </td>
                          <td className="text-center">
                            <span className={`status-badge ${statusClass}`}>
                              {getStatusLabel(entry)}
                            </span>
                          </td>
                          <td className="text-center actions-cell" onClick={(e) => e.stopPropagation()}>
                            <div className="actions-group">
                              {canAct && (
                                <>
                                  <button
                                    className={`pay-toggle ${entry.is_paid ? 'unpay' : 'pay'}`}
                                    onClick={() => handleTogglePaid(entry)}
                                    disabled={togglingMonth === entry.reference_month}
                                    title={entry.is_paid ? 'Marcar como não pago' : 'Marcar como pago'}
                                  >
                                    {togglingMonth === entry.reference_month ? '...' : entry.is_paid ? '↩' : '✓'}
                                  </button>
                                  <a
                                    href={SICALC_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="darf-link"
                                    title="Gerar DARF no Sicalc"
                                  >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                      <polyline points="14 2 14 8 20 8"/>
                                      <line x1="16" y1="13" x2="8" y2="13"/>
                                      <line x1="16" y1="17" x2="8" y2="17"/>
                                      <polyline points="10 9 9 9 8 9"/>
                                    </svg>
                                  </a>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="detail-row">
                            <td colSpan={14}>
                              <div className="sells-detail">
                                <table className="sells-table">
                                  <thead>
                                    <tr>
                                      <th>Data</th>
                                      <th>Ticker</th>
                                      <th className="text-right">Qtde</th>
                                      <th className="text-right">Preço Médio</th>
                                      <th className="text-right">Preço Venda</th>
                                      <th className="text-right">Custo Total</th>
                                      <th className="text-right">Valor Venda</th>
                                      <th className="text-right">Resultado</th>
                                      <th className="text-right">IRRF</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {entry.sells.map((sell, idx) => (
                                      <tr key={idx}>
                                        <td>{formatDate(sell.date)}</td>
                                        <td className="ticker-cell">{sell.ticker}</td>
                                        <td className="text-right">{sell.quantity}</td>
                                        <td className="text-right">{formatCurrency(sell.average_price)}</td>
                                        <td className="text-right">{formatCurrency(sell.sale_price)}</td>
                                        <td className="text-right">{formatCurrency(sell.cost_basis)}</td>
                                        <td className="text-right">{formatCurrency(sell.sale_value)}</td>
                                        <td className={`text-right ${sell.gain_loss >= 0 ? 'positive' : 'negative'}`}>
                                          {formatCurrency(sell.gain_loss)}
                                        </td>
                                        <td className="text-right">{sell.irrf > 0 ? formatCurrency(sell.irrf) : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {entry.accumulated_loss_before > 0 && entry.gross_result > 0 && (
                                  <div className="loss-compensation-note">
                                    Compensação de prejuízo: {formatCurrency(entry.accumulated_loss_before)} →
                                    Base tributável reduzida para {formatCurrency(entry.taxable_profit)}
                                  </div>
                                )}
                                {entry.accumulated_loss_after > 0 && (
                                  <div className="loss-carry-note">
                                    Prejuízo acumulado a compensar: {formatCurrency(entry.accumulated_loss_after)}
                                  </div>
                                )}
                                {entry.should_accumulate && (
                                  <div className="accumulate-note">
                                    Valor da DARF ({formatCurrency(entry.darf_amount)}) inferior a R$10,00 — acumulado para o próximo período com imposto.
                                  </div>
                                )}
                                {(entry.irrf_compensado > 0 || entry.irrf_acumulado_before > 0 || entry.irrf_acumulado_after > 0) && (
                                  <div className="irrf-note">
                                    IRRF retido no mês: <strong>{formatCurrency(entry.irrf_retido_mes)}</strong>
                                    {entry.irrf_acumulado_before > 0 && (
                                      <> · Saldo anterior: <strong>{formatCurrency(entry.irrf_acumulado_before)}</strong></>
                                    )}
                                    {entry.irrf_compensado > 0 && (
                                      <> · Compensado nesta DARF: <strong>−{formatCurrency(entry.irrf_compensado)}</strong></>
                                    )}
                                    {entry.irrf_acumulado_after > 0 && (
                                      <> · Saldo a compensar nos próximos meses: <strong>{formatCurrency(entry.irrf_acumulado_after)}</strong></>
                                    )}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data.by_year && data.by_year.length > 0 && (
            <div className="year-summary-section">
              <button
                type="button"
                className="year-summary-toggle"
                onClick={() => setShowYearSummary(s => !s)}
              >
                <span className={`expand-icon ${showYearSummary ? 'expanded' : ''}`}>▶</span>
                Resumo Anual para Declaração (DIRPF)
              </button>
              {showYearSummary && (
                <div className="year-summary-content">
                  <div className="info-banner">
                    Use estes valores na sua Declaração de Imposto de Renda. O <strong>IRRF Retido</strong> deve ser informado em "Rendimentos Sujeitos à Tributação Exclusiva/Definitiva".
                    O saldo de <strong>IRRF Não Compensado</strong> no ano-calendário pode ser informado como imposto pago/retido para fins de restituição.
                  </div>
                  <div className="darf-table-container">
                    <table className="darf-table">
                      <thead>
                        <tr>
                          <th>Ano</th>
                          <th className="text-right">Vendas Totais</th>
                          <th className="text-right">Ganhos</th>
                          <th className="text-right">Prejuízos</th>
                          <th className="text-right">DARF Devida</th>
                          <th className="text-right">DARF Paga</th>
                          <th className="text-right">IRRF Retido</th>
                          <th className="text-right">IRRF Compensado</th>
                          <th className="text-right">IRRF Não Compensado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.by_year.map((y: DarfYearSummary) => (
                          <tr key={y.year}>
                            <td className="month-cell">{y.year}</td>
                            <td className="text-right">{formatCurrency(y.total_sales)}</td>
                            <td className="text-right positive">{formatCurrency(y.total_gains)}</td>
                            <td className="text-right negative">{formatCurrency(y.total_losses)}</td>
                            <td className="text-right">{formatCurrency(y.total_tax_due)}</td>
                            <td className="text-right">{formatCurrency(y.total_tax_paid)}</td>
                            <td className="text-right">{formatCurrency(y.total_irrf_retido)}</td>
                            <td className="text-right">{formatCurrency(y.total_irrf_compensado)}</td>
                            <td className="text-right tax-has-value">
                              {formatCurrency(y.irrf_nao_compensado)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FIIDarf;
